import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import Input from 'components/Input'
import Select from 'components/Select'
import { useSelector } from 'react-redux'
import { selectTeachersList } from 'modules/teachers/ducks/selectors'
import { LESSON_TYPES_SELECT_OPTIONS } from 'utils/lessons'
import { selectSchoolSubjects } from 'modules/settings/ducks/selectors'
import Button from 'components/Button'
import { v4 as uuid } from 'uuid'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import { getSingleTeacherRates } from 'services/TeachersService'
import { dissoc, values as ramdaValues } from 'ramda'

const emptyValues = {
  studentRate: '',
  teacherRate: '',
  lessonType: null,
  lessonDuration: '',
  id: '',
  name: '',
  subject: ''
}

const AddStudentTeacherForm = ({ onAddTeacher, handleCloseModal }) => {
  const [values, setValues] = useState(emptyValues)
  const [teacherRates, setTeacherRates] = useState([])
  const teachers = useSelector(selectTeachersList)
  const subjects = useSelector(selectSchoolSubjects)

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const teacherRatesOptions = useMemo(() => {
    return teacherRates.map(rate => ({
      label: rate.name,
      value: rate.rate
    }))
  }, [teacherRates])

  useEffect(() => {
    isNotNilOrEmpty(values.id) && getSingleTeacherRates(values.id, setTeacherRates)
  }, [values.id])

  const teachersOptions = useMemo(() => {
    return teachers.map(teacher => ({
      label: teacher.name,
      value: teacher.id
    }))
  }, [teachers])

  const subjectsOptions = useMemo(() => {
    return subjects.map(subject => ({
      label: subject.name,
      value: subject.name
    }))
  }, [teachers])

  const handleAdd = () => {
    onAddTeacher({
      ...values,
      entryId: uuid()
    })
    handleCloseModal()
  }

  const isValid = useMemo(() => {
    return isNotNilOrEmpty(values.name)
  }, [values])

  return (
      <Form>
        <Select
          name='id'
          options={teachersOptions}
          label='Nauczyciel'
          onChange={(_, value) => {
            const teacherName = teachersOptions.find(o => o.value === value).label
            setValues(prev => ({
              ...prev,
              name: teacherName,
              id: value
            }))
          }}
        />
        <Row>
          <Select
            name='lessonType'
            options={LESSON_TYPES_SELECT_OPTIONS}
            label='Typ lekcji'
            onChange={handleValueChange}
            value={values.lessonType}
          />
          <Select
            name='subject'
            options={subjectsOptions}
            label='Przedmiot'
            onChange={handleValueChange}
            value={values.subject}
          />
        </Row>
        <Row>
          <Input
            name='lessonDuration'
            value={values.lessonDuration}
            label='Czas trwania lekcji'
            onChange={handleValueChange}
          />
          <Input
            name='studentRate'
            value={values.studentRate}
            label='Stawka ucznia'
            onChange={handleValueChange}
          />
        </Row>
        <Row>
          <Select
            name='ratesHelper'
            options={teacherRatesOptions}
            label='Wybór stawek'
            disabled={isNilOrEmpty(values.id) || isNilOrEmpty(teacherRates)}
            onChange={(_, value) => handleValueChange('teacherRate', value)}
          />
          <Input
            name='teacherRate'
            value={values.teacherRate}
            label='Stawka nauczyciela'
            onChange={handleValueChange}
          />
        </Row>
        <ButtonWrapper>
          <Button
            onClick={handleCloseModal}
            color='primary-outline'
          >
            Anuluj
          </Button>
          <Button
            onClick={handleAdd}
            color='primary'
            disabled={!isValid}
          >
            Dodaj nauczyciela
          </Button>
        </ButtonWrapper>
      </Form>
  )
}

export default AddStudentTeacherForm

const Form = styled.form`
  margin-top: 10px;
  width: 100%;
`

const Row = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
  align-items: center;
  
  & > div {
    max-width: 191px;
  }

  ${({ inModal }) => inModal && css`
    flex-direction: column;

    & > div {
      width: 100%;
    }
  `}
`

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 20px;

  button {
    max-width: 200px;
  }
`
